

import React, { useState } from 'react';
import { Box, IconButton, Tooltip, CircularProgress, Snackbar, Alert } from '@mui/material';
import { Edit, FileCopy, Delete, Save, Close, Email, PersonAddAlt1 } from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { commonIconButtonStyles, commonIconStyles, commonActionButtonStyles } from 'assets/theme/components/iconButtonStyles';

const LoadingIconButton = ({ loading, onClick, icon, title, disabled, sx = {}, onDisabledClick }) => (
  <Tooltip title={title} placement="top">
    <span onClick={disabled && onDisabledClick ? onDisabledClick : undefined}>
      <IconButton
        onClick={!disabled ? onClick : undefined}
        disabled={loading || disabled}
        sx={{
          ...commonIconButtonStyles,
          position: 'relative',
          ...(disabled && {
            backgroundColor: '#e0e0e0 !important',
            color: '#9e9e9e !important',
          }),
          '&.Mui-disabled': {
            backgroundColor: '#e0e0e0 !important',
            color: '#9e9e9e !important',
            cursor: 'not-allowed',
          },
          ...sx
        }}
      >
        {loading ? (
          <CircularProgress
            size={24}
            sx={{
              color: '#ffffff',
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: '-12px',
              marginLeft: '-12px',
            }}
          />
        ) : (
          icon
        )}
      </IconButton>
    </span>
  </Tooltip>
);

const LoadingActionButton = ({ loading, onClick, icon, title, disabled }) => (
  <MDButton
    variant="contained"
    onClick={onClick}
    sx={commonActionButtonStyles}
    disabled={loading || disabled}
  >
    {loading ? (
      <CircularProgress size={24} sx={{ color: '#ffffff', mr: 1 }} />
    ) : (
      icon
    )}
    {title}
  </MDButton>
);

const LeadDetailButtons = ({
  isEditing,
  isLeadLocked,
  handleEditToggle,
  handleCloneLead,
  handleOpenDeleteModal,
  handleSaveChanges,
  setIsEditing,
  handleDiscardChanges,
  handleToggleEmailModal,
  canConvertToContact = false,
  handleConvertToContact,
  isConvertToContactLoading = false,
  isEditLoading = false,
  isCloneLoading = false,
  isDeleteLoading = false,
  isSaveLoading = false,
  isCancelLoading = false,
  isEmailLoading = false,
}) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error',
  });

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Vertical spacing lives on the header row in LeadDetail, not here, so the
  // call and queue actions sitting beside this group share its baseline.
  return (
    <MDBox>
      {isEditing ? (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <LoadingActionButton
            loading={isCancelLoading}
            onClick={handleDiscardChanges}
            icon={<Close sx={commonIconStyles} />}
            title="Cancel"
            disabled={isSaveLoading || isCancelLoading}
          />
          <LoadingActionButton
            loading={isSaveLoading}
            onClick={handleSaveChanges}
            icon={<Save sx={commonIconStyles} />}
            title="Save"
            disabled={isSaveLoading || isCancelLoading}
          />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>

          {!isLeadLocked && handleEditToggle && (
            <LoadingIconButton
              loading={isEditLoading}
              onClick={handleEditToggle}
              icon={<Edit sx={commonIconStyles} />}
              title="Edit Lead"
            />
          )}

          <LoadingIconButton
            loading={isEmailLoading}
            onClick={handleToggleEmailModal}
            onDisabledClick={() => {
              setSnackbar({
                open: true,
                message: "Cannot send emails for qualified or disqualified leads",
                severity: "error"
              });
            }}
            icon={
              <Email
                sx={{
                  ...commonIconStyles,
                  ...(isLeadLocked && { color: "#9e9e9e" })
                }}
              />
            }
            title="Send Email"
            disabled={isLeadLocked}
            sx={{
              ...(isLeadLocked && {
                backgroundColor: "#f0f0f0",
                color: "#9e9e9e",
                pointerEvents: "none",
              }),
            }}
          />

            {( handleCloneLead &&
                <LoadingIconButton
                    loading={isCloneLoading}
                    onClick={handleCloneLead}
                    icon={<FileCopy sx={commonIconStyles}/>}
                    title="Clone Lead"
                />
            )}

          {canConvertToContact && typeof handleConvertToContact === 'function' && (
            <LoadingIconButton
              loading={isConvertToContactLoading}
              onClick={handleConvertToContact}
              icon={<PersonAddAlt1 sx={commonIconStyles} />}
              title="Convert to Contact (no deal)"
            />
          )}

          {!isLeadLocked && handleOpenDeleteModal && (
            <LoadingIconButton
              loading={isDeleteLoading}
              onClick={handleOpenDeleteModal}
              icon={<Delete sx={commonIconStyles} />}
              title="Delete Lead"
            />
          )}
        </Box>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MDBox>
  );
};

export default LeadDetailButtons;